import {
  useCallback, useContext, useMemo, useState,
} from 'react';
import { useLoadReport, useLoadShema } from './loader';
import { AppContext } from '../../../providers/authProvider';
import { useGroups } from './groups';
import { useSelections } from './selections';
import { useOrders } from './orders';
import { useFilters } from './filters';
import { useGenerateReport } from './render';
import { useParams } from './params';
import api from '../../../api/req';
import { REPORTS_API_URL } from './consts';
import { useColumns } from './columns';
import { downloadFileFromJSON } from '../../../api/utils';

const emptyReportData = {
  name: '',
  description: '',
  shema: null,
  options: {
    group: [],
    order: [],
    fields: [],
  },
  available_actions: {
    report_group_edit: false,
    report_column_edit: false,
    report_filter_edit: false,
    report_fields_edit: false,
    report_params_edit: false,
    report_order_edit: false,
  },
};

const emptySchema = {
  src: {
    fields: {},
    meta_fields: {},
  },
};

/**
 *
 * @param reportId {number} report id
 * @returns {{
 *  name: string,
 *  description: string,
 *  shema: Object,
 *
 *  groups: [],
 *  onSetGroups: (function(): null),
 *  onSetSelections: (function(): null),
 *  availableGroups: [],
 *  availableSelections: [],
 *  availableParams: [],
 *  columns: [],
 *  onSetColumns: (function(): null),
 *  availableColumns: [],
 *  filters: [],
 *  loading: boolean,
 *  error: string,
 *  selections: [],
 *  availableOrders: [],
 *  onSetFilters: (function(): null),
 *  onGenerateReport: (function(): null),
 *  onGenerateReportXLSX: (function(): null),
 *  onGenerateReportPDF: (function(): null),
 *  onGenerateReportHTML: (function(): null),
 *  onSaveReport: (function(): null),
 *  availableFilters: [],
 *  orders: [],
 *  onSetOrders: (function(): null),
 *  results: {},
 *  onGetSettings: (function(): {}),
 *  permissions: {
 *    groups: boolean,
 *    columns: boolean,
 *    filters: boolean,
 *    fields: boolean,
 *    params: boolean,
 *    orders: boolean,
 *  },
 *  }}
 */
// eslint-disable-next-line import/prefer-default-export
export const useReportEditor = (reportId) => {
  const { auth } = useContext(AppContext);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [reportData, setReportData] = useState(emptyReportData);
  const [schema, setSchema] = useState(emptySchema);

  const [results, setResults] = useState(null);

  const onLoadStart = useCallback(() => {
    setLoading(true);
    setErr(null);
  }, []);
  const onLoadEnd = useCallback(() => {
    setLoading(false);
  }, []);

  const onError = useCallback((e) => setErr(e), []);

  useLoadReport(reportId, auth, onLoadStart, onLoadEnd, setReportData, onError);
  useLoadShema(reportData, auth, onLoadStart, onLoadEnd, setSchema, onError);

  const {
    groups,
    availableGroups,
    displayGroups,
    groupsHandlers,
  } = useGroups(reportData, schema);

  const {
    columns,
    availableColumns,
    displayColumns,
    columnsHandlers,
  } = useColumns(reportData, schema);

  const {
    selections,
    availableSelections,
    displaySelections,
    selectionsHandlers,
  } = useSelections(reportData, schema);

  const {
    orders,
    availableOrders,
    displayOrders,
    ordersHandlers,
  } = useOrders(reportData, schema);

  const {
    filters,
    availableFilters,
    displayFilters,
    filtersHandlers,
  } = useFilters(reportData, schema);

  const {
    params,
    availableParams,
    displayParams,
    // changeParams,
    paramsHandlers,
  } = useParams(reportData, schema);

  const onGetSettings = useCallback(() => ({
    groups, selections, orders, filters, columns, params,
  }), [columns, filters, groups, orders, params, selections]);

  const { onGenerateReport, onAbortReport } = useGenerateReport(
    reportId,
    auth,
    groups,
    selections,
    orders,
    filters,
    columns,
    params,
    onLoadStart,
    onLoadEnd,
    setResults,
    onError,
  );

  const onFile = (d) => downloadFileFromJSON(d.file, d.filename);

  const onGenerateReportXLSX = useGenerateReport(
    reportId,
    auth,
    groups,
    selections,
    orders,
    filters,
    columns,
    params,
    onLoadStart,
    onLoadEnd,
    onFile,
    onError,
    'xlsx',
  );

  const onGenerateReportHTML = useGenerateReport(
    reportId,
    auth,
    groups,
    selections,
    orders,
    filters,
    columns,
    params,
    onLoadStart,
    onLoadEnd,
    onFile,
    onError,
    'html',
  );

  const onGenerateReportPDF = useGenerateReport(
    reportId,
    auth,
    groups,
    selections,
    orders,
    filters,
    columns,
    params,
    onLoadStart,
    onLoadEnd,
    onFile,
    onError,
    'pdf',
  );

  const onSaveReport = useCallback(
    (saveParam, isNew) => {
      const loader = async () => {
        const plainGroups = new Set(groups.reduce((R, g) => [...R, ...Object.keys(g)], []));
        const plainColumns = new Set(columns.reduce((R, g) => [...R, ...Object.keys(g)], []));
        const fields = [
          ...[...plainGroups].map((g) => [g, null]),
          ...[...plainColumns].map((g) => [g, null]),
          ...selections,
        ];

        const saverReportData = {
          schema,
          ...saveParam,
          options: {
            group: groups,
            column: columns,
            order: orders,
            filters,
            fields,
            params,
          },
        };
        const saver = isNew ? api.post : api.put;
        const URL = isNew ? REPORTS_API_URL : `${REPORTS_API_URL}${reportId}/`;
        const r = await saver(URL, auth, saverReportData);
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json();
      };
      onLoadStart();
      loader()
        .then((r) => !isNew && setReportData(r))
        .catch((e) => onError(e.message))
        .finally(() => onLoadEnd());
    },
    [auth, columns, filters, groups, onError, onLoadEnd, onLoadStart, orders, params, reportId, schema, selections],
  );

  const permissions = useMemo(
    () => ({
      groups: reportData.available_actions.report_group_edit,
      columns: reportData.available_actions.report_column_edit,
      filters: reportData.available_actions.report_filter_edit,
      fields: reportData.available_actions.report_fields_edit,
      params: reportData.available_actions.report_params_edit,
      orders: reportData.available_actions.report_order_edit,
    }),
    [reportData.available_actions.report_column_edit, reportData.available_actions.report_fields_edit, reportData.available_actions.report_filter_edit, reportData.available_actions.report_group_edit, reportData.available_actions.report_order_edit, reportData.available_actions.report_params_edit],
  );

  return {
    // name: reportData.name,
    // description: reportData.description,
    name: reportData.name,
    description: reportData.description,
    reportData,
    schema,
    loading,
    error: err,
    // groups
    availableGroups,
    groups: displayGroups,
    groupsHandlers,
    // selections
    availableSelections,
    selections: displaySelections,
    selectionsHandlers,
    // orders
    availableOrders,
    orders: displayOrders,
    ordersHandlers,
    // params
    availableParams,
    params: displayParams,
    paramsHandlers,
    // filters
    availableFilters,
    filters: displayFilters,
    filtersHandlers,
    // columns
    availableColumns,
    columns: displayColumns,
    columnsHandlers,
    onGenerateReport,
    onGenerateReportXLSX,
    onGenerateReportHTML,
    onGenerateReportPDF,
    results,
    onSaveReport,
    onGetSettings,
    onAbortReport,
    permissions,
    // onNewReport,
    //
  };
};
